/**
 * ActivateSubscriptionModal — «Отметить оплату»: команда Skrezo открывает
 * клинике доступ до выбранного дня (оплата переводом на счёт провайдер не
 * видит). Быстрые сроки считаются от конца текущего периода, если он ещё
 * впереди, иначе от сегодня. Повторная отметка снимает и блокировку.
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import dayjs, {type Dayjs} from 'dayjs';

import {activateSubscription} from '@services/clients';
import {keys} from '@queries/keys';
import {Field, TextInput} from '@components/FormKit';
import {cn} from '@lib/utils';

import {ModalShell} from '../ModalShell';

const TERMS = [1, 3, 6, 12] as const;

const fmtDay = (d: Dayjs): string => d.format('D MMM YYYY').replace('.', '');

type Props = {
  open: boolean;
  clientId: string;
  clientName: string;
  /** Конец текущего оплаченного периода (ISO) — от него считаем продление */
  currentPeriodEnd: string | null;
  onClose: () => void;
  onSaved: (paidUntil: string) => void;
};

export const ActivateSubscriptionModal = ({
  open,
  clientId,
  clientName,
  currentPeriodEnd,
  onClose,
  onSaved,
}: Props) => {
  const queryClient = useQueryClient();
  const today = dayjs().startOf('day');
  const periodEnd = currentPeriodEnd !== null ? dayjs(currentPeriodEnd) : null;
  const base = periodEnd !== null && periodEnd.isAfter(today) ? periodEnd : today;
  const termDate = (months: number) => base.add(months, 'month').format('YYYY-MM-DD');

  const [date, setDate] = useState(termDate(1));
  const [prevOpen, setPrevOpen] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setDate(termDate(1));
  }

  const mutation = useMutation({
    mutationFn: activateSubscription,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      void queryClient.invalidateQueries({queryKey: keys.dashboard.all});
      onSaved(date);
    },
  });

  const valid = dayjs(date).isValid() && !dayjs(date).isBefore(today);
  const handleSubmit = () => mutation.mutate({id: clientId, paidUntil: date});

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Отметить оплату"
      subtitle={
        <>
          <span className="font-semibold text-foreground">«{clientName}»</span> получит полный
          доступ сразу — режим чтения снимается без ожидания.
        </>
      }
      submitLabel={valid ? `Открыть до ${fmtDay(dayjs(date))}` : 'Открыть доступ'}
      onSubmit={handleSubmit}
      submitDisabled={!valid}
      pending={mutation.isPending}
      error={mutation.isError ? 'Не удалось отметить оплату. Попробуйте ещё раз' : null}
    >
      <Field label="Срок">
        <div className="grid grid-cols-4 gap-1.5">
          {TERMS.map((m) => {
            const on = date === termDate(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => setDate(termDate(m))}
                className={cn(
                  'h-10 rounded-[10px] border text-[13px] font-semibold transition',
                  on
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-input text-skr-ink2 hover:border-primary/50',
                )}
              >
                {m} мес
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Оплачено по (включительно)">
        <TextInput
          type="date"
          value={date}
          min={today.format('YYYY-MM-DD')}
          onChange={(e) => setDate(e.target.value)}
          mono
        />
      </Field>
      <div className="text-[12px] leading-[1.45] text-muted-foreground">
        {periodEnd !== null && periodEnd.isAfter(today)
          ? `Сейчас оплачено по ${fmtDay(periodEnd)} — срок продлевается от этой даты.`
          : 'Оплаченного периода впереди нет — срок считается от сегодня.'}
      </div>
    </ModalShell>
  );
};
